import { Link } from "react-router-dom";
import {
  CalendarIcon,
  HomeIcon,
  SettingsIcon,
  UserIcon,
  UsersIcon,
} from "../../components/Icons";
import { useAuth } from "../../contexts/AuthContext";

interface CardProps {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
}

function DashboardCard({ to, icon: Icon, title, description }: CardProps) {
  return (
    <Link
      to={to}
      className="block rounded-xl border border-[var(--color-border-default)] bg-white p-5 no-underline shadow-[0_2px_8px_rgba(0,0,0,0.05)] transition-all duration-200 hover:-translate-y-0.5 hover:border-[var(--color-primary-500)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)]"
    >
      <div className="mb-2 flex items-center gap-3">
        <Icon className="h-5 w-5 text-[var(--color-primary-500)]" />
        <h3 className="font-bold text-[var(--color-primary-500)] text-lg">
          {title}
        </h3>
      </div>
      <p className="text-[13px] text-[var(--color-text-secondary)]">
        {description}
      </p>
    </Link>
  );
}

export function Dashboard() {
  const { user } = useAuth();
  const isAdmin = !!user?.isAdmin;
  // 認証済みユーザーは全員 interviewer 扱い（admin と排他ではない）
  const isInterviewer = !!user;

  return (
    <div className="min-h-[calc(100vh-200px)] bg-[var(--color-bg-subtle-gray)] p-6">
      <div className="w-full">
        <div className="mb-4">
          <nav className="flex items-center gap-2 text-[13px] text-[var(--color-text-secondary)]">
            <span className="font-medium text-[var(--color-text-primary)]">
              ダッシュボード
            </span>
          </nav>
        </div>

        <div className="mb-8">
          <div className="border-[var(--color-primary-500)] border-l-4 pl-5">
            <h1 className="mb-2 font-bold text-[32px] text-[var(--color-primary-500)]">
              ダッシュボード
            </h1>
            <p className="font-medium text-[var(--color-text-secondary)] text-sm">
              {user?.email}
            </p>
          </div>
        </div>

        {isAdmin && (
          <div className="mb-8">
            <h2 className="mb-4 font-bold text-[var(--color-primary-500)] text-lg">
              管理者
            </h2>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-4">
              <DashboardCard
                to="/employee_interview/sponsors"
                icon={HomeIcon}
                title="スポンサー一覧"
                description="スポンサーの管理"
              />
              <DashboardCard
                to="/employee_interview/users"
                icon={UserIcon}
                title="ユーザー一覧"
                description="ユーザーの管理"
              />
              <DashboardCard
                to="/employee_interview/interviews"
                icon={UsersIcon}
                title="インタビュー一覧"
                description="全インタビューの管理"
              />
            </div>
          </div>
        )}

        {isInterviewer && (
          <div>
            <h2 className="mb-4 font-bold text-[var(--color-primary-500)] text-lg">
              インタビュアー
            </h2>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-4">
              <DashboardCard
                to="/employee_interview/interviewer/dashboard"
                icon={CalendarIcon}
                title="担当インタビュー"
                description="担当インタビューの管理"
              />
              <DashboardCard
                to="/employee_interview/google-connect"
                icon={SettingsIcon}
                title="カレンダー連携"
                description="Googleカレンダー設定"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
